import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, apiFetch } from '../App';
import { Bell } from 'lucide-react';
import LiveTicker from './LiveTicker';

const NAV = [
  { to:'/',            icon:'📊', label:'Markets'     },
  { to:'/league',      icon:'🏆', label:'League'      },
  { to:'/leaderboard', icon:'🥇', label:'Leaderboard' },
  { to:'/store',       icon:'🛍️', label:'Store'       },
  { to:'/profile',     icon:'👤', label:'Profile'     },
];

export default function Layout() {
  const { user, logout, theme, toggleTheme } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = React.useRef(null);
  const unread = notifs.filter(n => !n.read).length;

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) return;
    apiFetch('/api/web/notifications').then(d => setNotifs(d.notifications||[])).catch(()=>{});
    const interval = setInterval(() => {
      apiFetch('/api/web/notifications').then(d => setNotifs(d.notifications||[])).catch(()=>{});
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  async function markAllRead() {
    await apiFetch('/api/web/notifications/read', { method:'POST', body:{} });
    setNotifs(prev => prev.map(n => ({...n, read:true})));
  }

  const Logo = () => (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <img src="/logo.png" alt="NaijaPredict" style={{ width:36, height:36, borderRadius:8 }}/>
      <div>
        <div className="brand-text" style={{ fontSize:16, lineHeight:1 }}>NAIJAPREDICT</div>
        <div style={{ fontSize:9, color:'var(--muted)', letterSpacing:.5 }}>Skill-based predictions</div>
      </div>
    </div>
  );

  const NotifDropdown = () => (
    <div ref={notifRef} style={{ position:'relative' }}>
      <button onClick={() => { setShowNotifs(!showNotifs); if(!showNotifs && unread > 0) markAllRead(); }}
        style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 8px', cursor:'pointer', color:'var(--text2)', display:'flex', alignItems:'center', gap:6, position:'relative' }}>
        <Bell size={15} strokeWidth={1.8}/>
        <span style={{ fontSize:12 }}>Alerts</span>
        {unread > 0 && <span style={{ background:'#ff4444', color:'#fff', fontSize:10, fontWeight:700, borderRadius:20, padding:'1px 6px' }}>{unread>9?'9+':unread}</span>}
      </button>
      {showNotifs && (
        <div style={{ position:'absolute', left:0, right:0, top:'110%', background:'var(--bg1)', border:'1px solid var(--border)', borderRadius:14, boxShadow:'0 8px 32px rgba(0,0,0,.4)', zIndex:300, maxHeight:360, overflowY:'auto', minWidth:280 }}>
          <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, fontWeight:700 }}>Notifications</span>
            {unread > 0 && <button onClick={markAllRead} style={{ fontSize:11, color:'var(--orange)', background:'none', border:'none', cursor:'pointer' }}>Mark all read</button>}
          </div>
          {notifs.length === 0 ? (
            <div style={{ padding:'24px', textAlign:'center', color:'var(--muted)', fontSize:13 }}>No notifications yet</div>
          ) : notifs.map(n => (
            <div key={n.id} style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', background: n.read?'transparent':'rgba(255,107,53,.04)', cursor:'pointer' }}
              onClick={() => apiFetch('/api/web/notifications/read', { method:'POST', body:{ id:n.id }}).then(() => setNotifs(prev => prev.map(x => x.id===n.id?{...x,read:true}:x)))}>
              <div style={{ fontSize:13, fontWeight:n.read?400:600, marginBottom:3 }}>{n.title}</div>
              <div style={{ fontSize:11, color:'var(--text2)', lineHeight:1.5 }}>{n.message}</div>
              <div style={{ fontSize:10, color:'var(--muted)', marginTop:4 }}>{new Date(n.created_at).toLocaleDateString('en-NG',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)' }}>

      {/* Sidebar */}
      <aside className={`sidebar${mobileOpen?' open':''}`} style={{
        width:'var(--sidebar-w)', flexShrink:0, background:'var(--bg1)',
        borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column',
        position:'fixed', top:0, left:0, height:'100vh', zIndex:100,
        transition:'transform .3s',
      }}>
        {/* Logo */}
        <div style={{ padding:'16px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <Logo/>
            <button onClick={toggleTheme} style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 8px', cursor:'pointer', fontSize:16, color:'var(--text2)', flexShrink:0 }}>
              {theme==='dark' ? '☀️' : '🌙'}
            </button>
          </div>
          <NotifDropdown/>
        </div>

        {/* Balance chip */}
        {user && (
          <div style={{ margin:'12px', background:'rgba(255,107,53,.08)', border:'1px solid rgba(255,107,53,.2)', borderRadius:12, padding:'10px 14px' }}>
            <div style={{ fontSize:10, color:'var(--muted)', marginBottom:2, letterSpacing:.5 }}>BALANCE</div>
            <div style={{ fontFamily:"'Righteous',sans-serif", fontSize:24, color:'var(--gold)', letterSpacing:1 }}>{user.balance} ⭐</div>
            <div style={{ fontSize:10, color:'var(--text2)', marginTop:1 }}>≈ ₦{((user.balance||0)*15).toLocaleString()}</div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex:1, padding:'8px', overflowY:'auto' }}>
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.to==='/'} onClick={() => setMobileOpen(false)} style={({ isActive }) => ({
              display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
              borderRadius:10, marginBottom:2, fontSize:14, fontWeight:500,
              color: isActive ? 'var(--orange)' : 'var(--text2)',
              background: isActive ? 'rgba(255,107,53,.1)' : 'transparent',
              borderLeft: isActive ? '3px solid var(--orange)' : '3px solid transparent',
              transition:'all .15s',
            })}>
              <span style={{ fontSize:18 }}>{n.icon}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div style={{ padding:'12px', borderTop:'1px solid var(--border)' }}>
          {user ? (
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', marginBottom:2 }}>{user.firstName}</div>
              <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10 }}>@{user.username||'user'}</div>
              <button className="btn-ghost" style={{ width:'100%', padding:'8px', fontSize:13 }} onClick={logout}>Sign out</button>
            </div>
          ) : (
            <button className="btn-primary" style={{ width:'100%' }} onClick={() => navigate('/auth')}>
              Sign in / Register
            </button>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:99 }}/>}

      {/* Main content */}
      <main style={{ flex:1, marginLeft:'var(--sidebar-w)', minHeight:'100vh', display:'flex', flexDirection:'column', overflow:'hidden', maxWidth:'100%' }}>

        {/* Mobile header */}
        <header className="mobile-header" style={{ display:'none', alignItems:'center', justifyContent:'space-between', padding:'0 12px', height:'var(--header-h)', borderBottom:'1px solid var(--border)', background:'var(--bg1)', position:'sticky', top:0, zIndex:50, width:'100%', boxSizing:'border-box' }}>
          <button onClick={() => setMobileOpen(true)} style={{ background:'none', border:'none', color:'var(--text)', fontSize:22 }}>☰</button>
          <Logo/>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {user && <div style={{ fontSize:13, color:'var(--gold)', fontWeight:700 }}>{user.balance}⭐</div>}
            <button onClick={toggleTheme} style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'4px 7px', cursor:'pointer', fontSize:14 }}>
              {theme==='dark' ? '☀️' : '🌙'}
            </button>
            {!user && <button className="btn-primary" style={{ padding:'6px 12px', fontSize:11 }} onClick={() => navigate('/auth')}>Login</button>}
          </div>
        </header>

        <div style={{ flex:1, padding:'16px', maxWidth:1100, width:'100%', margin:'0 auto', boxSizing:'border-box', overflowX:'hidden' }}>
          <LiveTicker/>
          <Outlet/>
        </div>
      </main>

      <style>{`
        @media (max-width: 768px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.open { transform: translateX(0) !important; }
          main { margin-left: 0 !important; width: 100%; overflow-x: hidden; }
          .mobile-header { display: flex !important; }
          .card { padding: 14px; }
        }
      `}</style>
    </div>
  );
}
